import { useState } from 'react'
import AgeGroupSelector from '../../components/AgeGroupSelector'
import SudokuGrid from './SudokuGrid'
import { generatePuzzle } from '../../lib/sudoku/generator'
import {
  AgeGroup,
  Difficulty,
  DIFFICULTY_LABELS,
  SudokuConfig,
  SudokuPuzzle,
  SudokuSize,
  SUDOKU_FILL_COUNTS,
  SUDOKU_SIZE_DEFAULTS,
} from '../../types'

// ─── Config ───────────────────────────────────────────────────────────────────

const defaultConfig: SudokuConfig = {
  ageGroup:   'young',
  size:       6,
  difficulty: 'easy',
}

const SIZE_OPTIONS: { value: SudokuSize; label: string; subLabel: string }[] = [
  { value: 4, label: '4 × 4', subLabel: '2×2 boxes' },
  { value: 6, label: '6 × 6', subLabel: '3×2 boxes' },
  { value: 9, label: '9 × 9', subLabel: '3×3 boxes' },
]

const DIFFICULTIES: Difficulty[] = ['easy', 'medium', 'hard']

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function SudokuPage() {
  const [config, setConfig]     = useState<SudokuConfig>(defaultConfig)
  const [puzzle, setPuzzle]     = useState<SudokuPuzzle | null>(null)
  const [userGrid, setUserGrid] = useState<number[]>([])
  const [selected, setSelected] = useState(-1)
  const [checked, setChecked]   = useState(false)
  const [revealed, setRevealed] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [confirmReveal, setConfirmReveal] = useState(false)
  const [withSolution, setWithSolution]   = useState(false)
  const [printing, setPrinting]           = useState(false)
  const [worksheetCount, setWorksheetCount] = useState(1)

  async function handlePrint() {
    if (!puzzle) return
    setPrinting(true)
    try {
      const { createElement }  = await import('react')
      const { downloadPDF }    = await import('../../lib/pdf/download')
      const { SudokuDocument } = await import('../../lib/pdf/sudoku')
      const { generatePuzzle: gen } = await import('../../lib/sudoku/generator')
      const puzzles = [
        puzzle,
        ...Array.from({ length: worksheetCount - 1 }, () => gen(config)),
      ]
      const filename = worksheetCount > 1
        ? `sudoku-worksheet-${worksheetCount}-${config.size}x${config.size}.pdf`
        : `sudoku-${config.size}x${config.size}-${config.difficulty}.pdf`
      await downloadPDF(createElement(SudokuDocument, { puzzles, withSolution }), filename)
    } finally {
      setPrinting(false)
    }
  }

  // ── Derived state ──────────────────────────────────────────────────────

  const isSolved =
    puzzle !== null &&
    !revealed &&
    userGrid.length > 0 &&
    puzzle.given.every(
      (given, i) => given !== 0 || (userGrid[i] !== 0 && userGrid[i] === puzzle.solution[i]),
    )

  const filledByUser = userGrid.filter((v, i) => puzzle && puzzle.given[i] === 0 && v !== 0).length
  const totalBlanks  = puzzle ? puzzle.given.filter((v) => v === 0).length : 0

  // ── Handlers ──────────────────────────────────────────────────────────

  function startGenerate(cfg: SudokuConfig) {
    setGenerating(true)
    setTimeout(() => {
      const p = generatePuzzle(cfg)
      setPuzzle(p)
      setUserGrid(new Array(cfg.size * cfg.size).fill(0))
      setSelected(-1)
      setChecked(false)
      setRevealed(false)
      setConfirmReveal(false)
      setGenerating(false)
    }, 20) // tiny defer so the spinner can render
  }

  function handleInput(idx: number, value: number) {
    if (revealed) return
    setUserGrid((prev) => {
      const next = [...prev]
      next[idx] = value
      return next
    })
    setChecked(false)
    setSelected(idx)
  }

  function handleSelect(idx: number) {
    setSelected((prev) => prev === idx ? -1 : idx)
  }

  function handleNewPuzzle() {
    if (puzzle) startGenerate(config)
  }

  // ── Screens ───────────────────────────────────────────────────────────

  if (!puzzle) {
    return (
      <ConfigScreen
        config={config}
        generating={generating}
        onAgeGroupChange={(ageGroup: AgeGroup) =>
          setConfig((p) => ({ ...p, ageGroup, size: SUDOKU_SIZE_DEFAULTS[ageGroup] }))
        }
        onSizeChange={(size: SudokuSize) => setConfig((p) => ({ ...p, size }))}
        onDifficultyChange={(difficulty: Difficulty) => setConfig((p) => ({ ...p, difficulty }))}
        onStart={() => startGenerate(config)}
      />
    )
  }

  return (
    <div className="max-w-lg mx-auto">
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display font-black text-2xl text-bubble-text">Sudoku</h1>
          <p className="text-sm text-bubble-muted">
            {config.size}×{config.size} · {DIFFICULTY_LABELS[config.difficulty]}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleNewPuzzle}
            disabled={generating}
            className="btn-secondary text-sm px-4 py-2"
          >
            {generating ? '⏳' : '↺'} New
          </button>
          <label className="flex items-center gap-1 text-xs text-bubble-muted font-black cursor-pointer select-none">
            <input
              type="checkbox"
              checked={withSolution}
              onChange={(e) => setWithSolution(e.target.checked)}
              className="accent-bubble-coral"
            />
            +solution
          </label>
          <select
            value={worksheetCount}
            onChange={(e) => setWorksheetCount(Number(e.target.value))}
            className="text-xs font-black text-bubble-text border-2 border-gray-200 rounded-full px-3 py-1.5 bg-white cursor-pointer focus:outline-none focus:border-bubble-coral"
            aria-label="Number of puzzles to print"
          >
            {[1, 2, 3, 5, 10].map((n) => (
              <option key={n} value={n}>{n === 1 ? '1 puzzle' : `${n} puzzles`}</option>
            ))}
          </select>
          <button
            onClick={handlePrint}
            disabled={printing}
            className="btn-primary text-sm px-4 py-2 disabled:opacity-60"
          >
            {printing ? '⏳ Generating…' : `🖨 ${worksheetCount > 1 ? 'Worksheet' : 'Print'}`}
          </button>
        </div>
      </div>

      {/* ── Solved banner ── */}
      {isSolved && (
        <div className="card border-bubble-teal bg-bubble-teal-light mb-5 p-4 text-center">
          <p className="font-display font-black text-xl text-bubble-teal mb-1">
            🎉 Solved!
          </p>
          <p className="text-sm text-teal-700">
            Great job! Try a harder one?
          </p>
        </div>
      )}

      {/* ── Progress bar ── */}
      {!isSolved && totalBlanks > 0 && (
        <div className="mb-4">
          <div className="flex justify-between text-xs text-bubble-muted font-black mb-1">
            <span>{filledByUser} / {totalBlanks} filled</span>
            <span>{Math.round((filledByUser / totalBlanks) * 100)}%</span>
          </div>
          <div className="h-2 bg-bubble-coral-light rounded-full overflow-hidden">
            <div
              className="h-full bg-bubble-coral rounded-full transition-all duration-300"
              style={{ width: `${(filledByUser / totalBlanks) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* ── Grid ── */}
      <SudokuGrid
        puzzle={puzzle}
        user={userGrid}
        selected={selected}
        checked={checked}
        revealed={revealed}
        onSelect={handleSelect}
        onInput={handleInput}
      />

      {/* ── Actions ── */}
      {!isSolved && !revealed && (
        <div className="flex gap-3 mt-6 justify-center">
          <button
            onClick={() => setChecked(true)}
            disabled={filledByUser === 0}
            className="btn-primary px-6 py-2.5 text-sm disabled:opacity-50"
          >
            Check answers
          </button>

          {!confirmReveal ? (
            <button
              onClick={() => setConfirmReveal(true)}
              className="btn-secondary px-6 py-2.5 text-sm"
            >
              Show solution
            </button>
          ) : (
            <div className="flex gap-2 items-center">
              <span className="text-xs text-bubble-muted font-black">Sure?</span>
              <button
                onClick={() => { setRevealed(true); setConfirmReveal(false) }}
                className="btn-secondary px-4 py-2 text-xs"
              >
                Yes, reveal
              </button>
              <button
                onClick={() => setConfirmReveal(false)}
                className="btn-ghost px-3 py-2 text-xs"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Check feedback ── */}
      {checked && !isSolved && !revealed && (
        <div className="mt-3 text-center">
          {(() => {
            const wrong = userGrid.filter(
              (v, i) => puzzle.given[i] === 0 && v !== 0 && v !== puzzle.solution[i],
            ).length
            return wrong > 0 ? (
              <p className="text-sm font-black text-red-500">
                {wrong} incorrect cell{wrong > 1 ? 's' : ''} — keep going!
              </p>
            ) : (
              <p className="text-sm font-black text-green-600">
                Looking good so far — keep going!
              </p>
            )
          })()}
        </div>
      )}
    </div>
  )
}

// ─── Config screen ────────────────────────────────────────────────────────────

interface ConfigScreenProps {
  config:             SudokuConfig
  generating:         boolean
  onAgeGroupChange:   (v: AgeGroup) => void
  onSizeChange:       (v: SudokuSize) => void
  onDifficultyChange: (v: Difficulty) => void
  onStart:            () => void
}

function ConfigScreen({
  config,
  generating,
  onAgeGroupChange,
  onSizeChange,
  onDifficultyChange,
  onStart,
}: ConfigScreenProps) {
  const filledCells = SUDOKU_FILL_COUNTS[config.size][config.difficulty]
  const totalCells  = config.size * config.size
  const blankCells  = totalCells - filledCells

  return (
    <div className="max-w-lg">
      <h1 className="font-display font-black text-3xl text-bubble-text mb-1">Sudoku</h1>
      <p className="text-bubble-muted mb-8">Set up your puzzle, then hit Generate.</p>

      <div className="card p-6 space-y-6">
        <AgeGroupSelector value={config.ageGroup} onChange={onAgeGroupChange} />

        {/* Grid size */}
        <div>
          <label className="config-label">Grid Size</label>
          <div className="flex gap-2" role="radiogroup" aria-label="Grid size">
            {SIZE_OPTIONS.map(({ value, label, subLabel }) => (
              <button
                key={value}
                role="radio"
                aria-checked={config.size === value}
                onClick={() => onSizeChange(value)}
                className={config.size === value ? 'sel-btn-active' : 'sel-btn'}
              >
                <span className="text-base font-black">{label}</span>
                <span className="text-xs font-normal text-bubble-muted mt-0.5">{subLabel}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Difficulty */}
        <div>
          <label className="config-label">Difficulty</label>
          <div className="flex gap-2" role="radiogroup" aria-label="Difficulty">
            {DIFFICULTIES.map((d) => (
              <button
                key={d}
                role="radio"
                aria-checked={config.difficulty === d}
                onClick={() => onDifficultyChange(d)}
                className={config.difficulty === d ? 'sel-btn-active' : 'sel-btn'}
              >
                {DIFFICULTY_LABELS[d]}
              </button>
            ))}
          </div>
          <p className="text-xs text-bubble-muted mt-1.5 font-black">
            {blankCells} blank cells to fill in ({totalCells} total)
          </p>
        </div>

        <button
          onClick={onStart}
          disabled={generating}
          className="btn-primary w-full text-base py-3"
        >
          {generating ? 'Generating…' : 'Generate Puzzle →'}
        </button>
      </div>
    </div>
  )
}
